import React, { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { sharedApi } from "@/lib/api";
import { Pencil } from "lucide-react";

// Module-level stable ref so useEffect deps below don't churn each render
// while the query is still resolving.
const EMPTY_AREAS = Object.freeze([]);

/**
 * Area picker (legacy parity, iter-213).
 * Contract: `GET /api/classifieds/areas?region&city` → { areas: [...] }
 *
 *   • When the backend returns 1+ suburbs → render a `<select>` PLUS a
 *     "✏ Type it manually" row that swaps the control back to a free-text
 *     input (for suburbs we haven't seeded yet).
 *   • When the backend returns an empty list → keep the plain free-text
 *     input (same UX as before).
 *
 * The parent still owns the `value` string; API contract unchanged.
 */
export default function AreaField({
  region,
  city,
  country = "NZ",
  value,
  onChange,
  testId = "area-field",
  disabled = false,
}) {
  const q = useQuery({
    queryKey: ["areas", country, region, city],
    queryFn: async () => {
      const { data } = await sharedApi.get("/classifieds/areas", {
        params: { country, region, city },
      });
      return data?.areas || [];
    },
    enabled: !!region && !!city,
    staleTime: 5 * 60_000,
  });

  const areas = useMemo(() => q.data || EMPTY_AREAS, [q.data]);
  // "manual" mode: user picked "✏ Type it manually" and we render a text input.
  const [manual, setManual] = useState(false);
  const hasList = areas.length > 0;

  // If the parent value doesn't match any option in the list, force manual mode
  // so the current value stays visible / editable (e.g. legacy data).
  useEffect(() => {
    if (hasList && value && !areas.includes(value)) setManual(true);
  }, [hasList, value, areas]);

  // When region/city change and the backend returns no options, drop manual
  // mode so the plain input is used.
  useEffect(() => {
    if (!hasList) setManual(false);
  }, [hasList, region, city]);

  if (!region || !city || !hasList || manual) {
    // Nudge users to pick region+city so they can see the suburb suggestions.
    // Without the hint, this state looks identical to the pre-iter-213 UX
    // and the "dropdown" feature feels invisible until a city is picked.
    const showListHint = !region || !city;
    return (
      <div>
        <div className="flex items-center gap-2">
          <input
            data-testid={testId}
            className="input flex-1"
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            placeholder="Suburb / area (free text)"
            maxLength={120}
            disabled={disabled}
          />
          {hasList && manual && (
            <button
              type="button"
              onClick={() => {
                setManual(false);
                onChange("");
              }}
              data-testid={`${testId}-use-list`}
              className="text-xs font-semibold text-[color:var(--brand-blue)] hover:underline whitespace-nowrap"
            >
              Choose from list
            </button>
          )}
        </div>
        {showListHint && (
          <div className="mt-1 text-[11px] text-slate-400" data-testid={`${testId}-list-hint`}>
            Pick a region and city first to see suburb suggestions.
          </div>
        )}
      </div>
    );
  }

  return (
    <select
      data-testid={`${testId}-select`}
      className="input"
      value={value || ""}
      disabled={disabled}
      onChange={(e) => {
        if (e.target.value === "__manual__") {
          setManual(true);
          onChange("");
          return;
        }
        onChange(e.target.value);
      }}
    >
      <option value="">Select suburb / area</option>
      {areas.map((a) => (
        <option key={a} value={a}>
          {a}
        </option>
      ))}
      <option value="__manual__">Type it manually</option>
    </select>
  );
}

// Small icon export in case a parent wants to badge the manual toggle.
export { Pencil as AreaFieldManualIcon };
